import React, { useEffect, useState } from "react";

// MUI components
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  IconButton,
  InputAdornment,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

// Asset components
import cross from "../../assets/close-circle.svg";

// Styles
import "./UserManagementFormDialog.scss";

// Config
import { appConfig } from "../../config/appConfig";

// Components
import Spinner from "../../components/spinner/Spinner";

// Services
import { getStorageItemLocal } from "../../services/storageServices";
import { logout } from "../../services/IndiaService";

// Hooks
import { useNavigate } from "react-router-dom";

type AdminEntry = {
  [x: string]: any;
  id: number;
  username: string;
  email: string;
  password: string;
  phonenumber: string;
  role: string;
};

interface UserManagementFormDialogProps {
  open: boolean;
  onClose?: () => void;

  fetchData: () => void;
  resetForm: () => void;

  formData: AdminEntry;
  setFormData: React.Dispatch<React.SetStateAction<AdminEntry>>;
  editingIndex: any;
  setShowForm: React.Dispatch<React.SetStateAction<boolean>>;
  setEditingIndex: React.Dispatch<React.SetStateAction<any>>;
  dataList: any;
}

const UserManagementFormDialog: React.FC<UserManagementFormDialogProps> = ({
  open,
  onClose,
  fetchData,
  resetForm,
  formData,
  setFormData,
  editingIndex,
  setShowForm,
  setEditingIndex,
  dataList,
}) => {
  // useState
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // useNavigate
  const navigate = useNavigate();

  // useEffect
  useEffect(() => {}, [formData]);

  // Methods
  const handleCancel = () => {
    onClose?.();
    setShowForm(false);
    setEditingIndex(null);
    resetForm();
    setShowForm(false);
  };

  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  const handleSave = async () => {
    if (!formData.username.trim()) {
      alert("Name is required!");
      return;
    }
    if (!formData.password.trim()) {
      alert("Password is required!");
      return;
    }
    const passwordRegex = /^(?=.*[A-Za-z])(?=.*\d).{7,16}$/;

    if (!passwordRegex.test(formData.password)) {
      alert(
        "Password must be 7-16 characters and include letters, numbers, and special characters."
      );
      return;
    }
    if (!formData.role.trim()) {
      alert("Role is required!");
      return;
    }
    if (!/^\d{10}$/.test(formData.phonenumber)) {
      alert("Phone number must be exactly 10 digits!");
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      alert("Invalid email format!");
      return;
    }
    setLoading(true);
    const formDataToSend = new FormData();
    formDataToSend.append("id", editingIndex?.toString() ?? "0");
    formDataToSend.append("username", formData.username);
    formDataToSend.append("email", formData.email);
    formDataToSend.append("password", formData.password);
    formDataToSend.append("phonenumber", formData.phonenumber);
    formDataToSend.append("role", formData.role);

    const username1 = getStorageItemLocal("username") ?? "";
    const username = username1 ? JSON.parse(username1) : null;
    formDataToSend.append("uploadBy", username);

    const url =
      editingIndex !== null
        ? `${appConfig.serviceUrls.saveLoginEntries}/${formData.id}`
        : `${appConfig.serviceUrls.saveLoginEntries}/upload`;

    try {
      const tokenString = getStorageItemLocal("token");
      const token = tokenString ? JSON.parse(tokenString) : null;
      const response = await fetch(url, {
        method: editingIndex !== null ? "PUT" : "POST",
        body: formDataToSend,

        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.status === 401) {
        logout(navigate);
        <Button onClick={() => logout(navigate)}>Logout</Button>;
        return;
      }
      if (!response.ok) throw new Error("Failed to save data");
      alert("Data saved successfully");
      fetchData();
      resetForm();
      setEditingIndex(null);
      setShowForm(false);
    } catch (error) {
      console.error("Error saving data:", error);
      // alert(`Failed to save data: ${error}`);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      fullWidth
      maxWidth={false}
      PaperProps={{
        sx: {
          width: "900px",
          height: "450px",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          backgroundColor: "#F7F7F7",
          padding: "20px 16px", // Optional: improves appearance
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <Typography className="custom-icon-text">
            {editingIndex !== null ? "Edit User" : "Add User"}
          </Typography>
        </Box>

        <Box>
          <IconButton
            color="primary"
            size="small"
            onClick={handleCancel}
            className="custom-icon-button2"
          >
            <img src={cross} alt="visible" className="custom-icon35" />
          </IconButton>
        </Box>
      </Box>

      {loading ? (
        <>
          {" "}
          <Spinner></Spinner>
        </>
      ) : (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-around",
            alignItems: "center",
            background: "#FFF",
            padding: "50px 16px",
          }}
        >
          {/* 1st */}
          <Box>
            <Typography className="text-style">User Name</Typography>
            <Box className="pdf-name-wrapper">
              <TextField
                placeholder="Enter User Name"
                size="small"
                name="username"
                value={formData.username}
                onChange={handleChange}
                variant="standard"
                fullWidth
                InputProps={{ disableUnderline: true }}
              />
            </Box>
          </Box>
          {/* 2nd */}
          <Box>
            <Typography className="text-style">Password</Typography>
            <Box className="pdf-name-wrapper">
              <TextField
                placeholder="Enter Password"
                size="small"
                name="password"
                type={showPassword ? "text" : "password"}
                value={formData.password}
                onChange={handleChange}
                variant="standard"
                fullWidth
                InputProps={{
                  disableUnderline: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={togglePasswordVisibility}
                        edge="end"
                        size="small"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          </Box>

          {/* 3rd */}
          <Box>
            <Typography className="text-style">Role</Typography>
            <Box className="pdf-name-wrapper">
              <TextField
                select
                placeholder="Select a Role"
                size="small"
                name="role"
                value={formData.role || "Admin"}
                onChange={(e) =>
                  setFormData({ ...formData, role: e.target.value })
                }
                variant="standard"
                fullWidth
                InputProps={{ disableUnderline: true }}
                SelectProps={{
                  MenuProps: {
                    PaperProps: {
                      sx: {
                        minWidth: "250px !important", // ensure MenuItem container matches TextField width
                      },
                    },
                  },
                }}
              >
                <MenuItem value="System Admin">System Admin</MenuItem>
                <MenuItem value="Admin User">Admin User</MenuItem>
              </TextField>
            </Box>
          </Box>
        </Box>
      )}
      {/* 4th and 5Th box */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-around",
          alignItems: "center",
          background: "#FFF",
          padding: "10px 16px",
        }}
      >
        <Box>
          <Typography className="text-style">Email Address</Typography>
          <Box className="pdf-name-wrapper">
            <TextField
              placeholder="Enter Email Address"
              size="small"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              variant="standard"
              fullWidth
              InputProps={{ disableUnderline: true }}
            />
          </Box>
        </Box>

        <Box>
          <Typography className="text-style">Phone Number</Typography>
          <Box className="pdf-name-wrapper">
            <TextField
              placeholder="Enter Phone Number"
              size="small"
              name="phonenumber"
              type="number"
              value={formData.phonenumber}
              onChange={handleChange}
              variant="standard"
              fullWidth
              InputProps={{ disableUnderline: true }}
              inputProps={{
                inputMode: "numeric",
                pattern: "[0-9]*",
                style: { appearance: "textfield" },
              }}
              sx={{
                "& input[type=number]": {
                  MozAppearance: "textfield",
                },
                "& input[type=number]::-webkit-outer-spin-button": {
                  WebkitAppearance: "none",
                  margin: 0,
                },
                "& input[type=number]::-webkit-inner-spin-button": {
                  WebkitAppearance: "none",
                  margin: 0,
                },
              }}
            />
          </Box>
        </Box>
      </Box>

      <DialogActions>
        <Button
          onClick={handleCancel}
          className="cancel-button"
          variant="outlined"
        >
          Cancel
        </Button>
        <Button onClick={handleSave} className="submit-button">
          {editingIndex !== null ? "Update" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UserManagementFormDialog;
